import * as Sentry from '@sentry/react';
import { toast } from 'sonner';

import { env } from '@/env';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Typed error for the admin CMS HTTP layer. Mirrors the shape of the server's
 * shared `ApiError` envelope (statusCode/message/code/details).
 */
export class AdminApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: Record<string, unknown>;

  constructor(init: {
    status: number;
    code?: string;
    message: string;
    details?: Record<string, unknown>;
  }) {
    super(init.message);
    this.name = 'AdminApiError';
    this.status = init.status;
    this.code = init.code;
    this.details = init.details;
  }
}

type Parse = 'json' | 'none';

interface AdminFetchInit extends Omit<RequestInit, 'body'> {
  body?: BodyInit | Record<string, unknown> | unknown[] | null;
  parse?: Parse;
}

/**
 * `fetch` wrapper used by the admin CMS layer. Returns parsed JSON or throws
 * `AdminApiError` on any non-2xx response. The body is JSON-stringified
 * automatically for plain objects; FormData / Blob / ArrayBuffer / strings
 * pass through unchanged so the same primitive can handle multipart and
 * presigned S3 PUT calls.
 *
 * Auth: injects `Authorization: Bearer <accessToken>` from the auth store
 * whenever a token exists.
 */
export async function adminApiFetch<T = unknown>(
  path: string,
  init: AdminFetchInit = {},
): Promise<T> {
  const { body, parse = 'json', headers: callerHeaders, ...rest } = init;
  const headers = new Headers(callerHeaders);

  const token = useAuthStore.getState().tokens?.accessToken;
  if (token && !headers.has('authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (!headers.has('accept')) {
    headers.set('Accept', 'application/json');
  }

  const finalBody = serializeBody(body, headers);

  let response: Response;
  try {
    response = await fetch(`${env.VITE_API_BASE_URL}${path}`, {
      ...rest,
      headers,
      body: finalBody,
    });
  } catch (err) {
    throw new AdminApiError({
      status: 0,
      code: 'network_error',
      message: err instanceof Error ? err.message : 'Network error',
    });
  }

  if (!response.ok) {
    throw await parseErrorResponse(response);
  }

  if (parse === 'none' || response.status === 204) {
    return undefined as T;
  }
  // Server may return empty body even on 200 (rare); guard against JSON parse error.
  const text = await response.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

function isJsonBody(body: unknown): body is Record<string, unknown> | unknown[] {
  if (body === null || body === undefined) return false;
  if (typeof body === 'string') return false;
  if (body instanceof FormData) return false;
  if (body instanceof Blob) return false;
  if (body instanceof ArrayBuffer) return false;
  if (body instanceof URLSearchParams) return false;
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(body as ArrayBufferView))
    return false;
  return typeof body === 'object';
}

function serializeBody(
  body: AdminFetchInit['body'],
  headers: Headers,
): BodyInit | null | undefined {
  if (body === undefined || body === null) return body ?? undefined;
  if (isJsonBody(body)) {
    if (!headers.has('content-type')) {
      headers.set('Content-Type', 'application/json');
    }
    return JSON.stringify(body);
  }
  return body as BodyInit;
}

interface MapApiErrorOptions {
  onConflict?: (err: AdminApiError) => void;
  onValidation?: (fields: Record<string, string>) => void;
  silent?: boolean;
  reloadHref?: string;
}

export interface MapApiErrorResult {
  handled: boolean;
  fields?: Record<string, string>;
}

/**
 * Central error → user feedback mapping for the admin CMS layer. The policy
 * is documented in docs/superpowers/specs/2026-05-20-admin-rbac-wiring.md §7.
 *
 * Returns `{ handled: true, fields? }` whenever it has surfaced an error to
 * the user; the `fields` map is populated for `validation_error` 400s so the
 * caller can pass it to RHF `setError`.
 */
export function mapApiError(error: unknown, opts: MapApiErrorOptions = {}): MapApiErrorResult {
  if (!(error instanceof AdminApiError)) {
    if (!opts.silent) toast.error('Network error. Check your connection.');
    Sentry.captureException(error);
    return { handled: true };
  }

  const { status, code, message, details } = error;

  if (status === 401) {
    useAuthStore.getState().clear();
    if (!opts.silent) toast.error('Session expired');
    return { handled: true };
  }

  if (status === 403) {
    if (!opts.silent) toast.error("You don't have permission to do that");
    return { handled: true };
  }

  if (status === 404) {
    if (!opts.silent) toast.error('Not found');
    return { handled: true };
  }

  if (status === 409) {
    if (opts.onConflict) {
      opts.onConflict(error);
      return { handled: true };
    }
    if (!opts.silent) {
      toast.error('This row changed since you opened it. Refresh and try again.', {
        action: {
          label: 'Reload',
          onClick: () => {
            if (typeof window !== 'undefined') {
              window.location.href = opts.reloadHref ?? window.location.href;
            }
          },
        },
      });
    }
    return { handled: true };
  }

  if (status === 400 && code === 'validation_error') {
    const fields = parseZodIssues(details);
    if (opts.onValidation) {
      opts.onValidation(fields);
    } else if (!opts.silent) {
      toast.error(message);
    }
    return { handled: true, fields };
  }

  if (status >= 400 && status < 500) {
    if (!opts.silent) toast.error(message);
    return { handled: true };
  }

  // 5xx (or unexpected status)
  if (!opts.silent) toast.error('Something went wrong. Please try again.');
  Sentry.captureException(error);
  return { handled: true };
}

interface ZodIssueLike {
  path: ReadonlyArray<string | number>;
  message: string;
}

function parseZodIssues(details: Record<string, unknown> | undefined): Record<string, string> {
  const issues = details?.issues;
  if (!Array.isArray(issues)) return {};
  const out: Record<string, string> = {};
  for (const raw of issues as unknown[]) {
    if (!raw || typeof raw !== 'object') continue;
    const issue = raw as Partial<ZodIssueLike>;
    if (!Array.isArray(issue.path) || typeof issue.message !== 'string') continue;
    const key = issue.path.length === 0 ? '_root' : issue.path.join('.');
    // First issue per field wins (matches RHF setError semantics).
    if (!(key in out)) out[key] = issue.message;
  }
  return out;
}

async function parseErrorResponse(response: Response): Promise<AdminApiError> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      const body = (await response.json()) as {
        message?: string;
        code?: string;
        details?: Record<string, unknown>;
      };
      return new AdminApiError({
        status: response.status,
        code: body.code,
        message: body.message ?? response.statusText ?? 'Request failed',
        details: body.details,
      });
    } catch {
      // fall through to non-JSON path
    }
  }
  return new AdminApiError({
    status: response.status,
    code: 'network_error',
    message: response.statusText || 'Request failed',
  });
}
